import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToOffer } from "@/components/Nav";

const HERO_SRCSET =
  "/images/hero-640.webp 640w, /images/hero-1024.webp 1024w, /images/hero-1920.webp 1920w";

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 160]);
  const opacity = useTransform(scrollY, [0, 700], [1, 0.25]);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden flex flex-col lg:min-h-screen">
      {/* Version ordinateur : image plein écran */}
      <motion.div style={{ y }} className="absolute inset-0 scale-[1.08] hidden lg:block">
        <img
          src="/images/hero-1920.webp"
          srcSet={HERO_SRCSET}
          sizes="100vw"
          alt="Silhouette face à un globe terrestre, titre MULTIPOLAR"
          width={1920}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#06120E]/50 via-[#06120E]/20 to-[#06120E] hidden lg:block" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#06120E]/70 via-transparent to-transparent hidden lg:block" />

      {/* Version mobile / tablette : image entière en haut, fondu vers le fond */}
      <div className="relative w-full mt-16 lg:hidden">
        <img
          src="/images/hero-640.webp"
          srcSet={HERO_SRCSET}
          sizes="100vw"
          alt="Silhouette face à un globe terrestre, titre MULTIPOLAR"
          width={1080}
          height={607}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-auto block"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-b from-transparent to-[#06120E]" aria-hidden="true" />
      </div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-10 md:pb-14 mt-8 lg:mt-auto"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex flex-wrap gap-2 lg:gap-3 mb-6"
        >
          <span className="micro-label" data-testid="hero-label-dossier">Programme vidéo</span>
          <span className="micro-label" data-testid="hero-label-ref">5 actes</span>
          <span className="micro-label !border-[#D92D20]/50 !text-[#FF6B00]" data-testid="hero-label-live">
            <span className="animate-pulse-line">Accès à vie</span>
          </span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="max-w-[65ch] text-[#D8D4CC] font-light"
        >
          L'ordre mondial bascule. La course technologique accélère. Les capitaux se déplacent.
          <br />
          MULTIPOLAR est un programme vidéo qui répond à une question : où doit regarder
          un investisseur pour comprendre la prochaine décennie ?
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-8 flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-4"
        >
          <button
            type="button"
            onClick={scrollToOffer}
            data-testid="hero-cta-button"
            className="bg-[#D92D20] hover:bg-[#b3241a] text-white font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 rounded-sm transition-[background-color,transform] duration-300 hover:scale-[1.02] text-center w-full md:w-auto"
          >
            Accéder
          </button>
          <a
            href="#programme"
            data-testid="hero-secondary-button"
            className="border border-white/20 hover:bg-white/5 text-[#F5F5F0] font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 rounded-sm transition-colors duration-300 text-center w-full md:w-auto"
          >
            Voir le programme
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 right-6 md:right-12 z-10 hidden lg:flex items-center gap-2 text-[#758D85]"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Défiler</span>
        <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
